import type { AbsenceType } from '@/types';

// ============================================================================
// TYPES D'ABSENCE / INDISPONIBILITÉ — source UNIQUE (couleurs + libellés)
// ============================================================================
// Miroir de officina-backend/src/services/absence-types.ts. Utilisé partout :
// menu Disponibilités, section Absences RH, planning, file Demandes, mobile.

export interface AbsenceMeta {
  label: string;
  short: string;
  dot: string; // pastille (hex)
  chip: string; // classes Tailwind pour un chip / badge
  cell: string; // classes pour une case pleine (calendrier / planning)
}

// Ordre d'affichage dans les listes déroulantes.
export const ABSENCE_TYPES: AbsenceType[] = [
  'CONGE_PAYE', 'CONGE_SANS_SOLDE', 'SICK', 'ACCIDENT_TRAVAIL', 'MATERNITY',
  'FAMILIAL', 'RECUPERATION', 'FORMATION', 'MEDICAL', 'PERSO', 'AUTRE',
];

export const ABSENCE_META: Record<AbsenceType, AbsenceMeta> = {
  CONGE_PAYE: { label: 'Congé payé', short: 'Congé', dot: '#64748b', chip: 'bg-slate-200 text-slate-700 border-slate-300', cell: 'bg-slate-200 text-slate-700 border-slate-400' },
  CONGE_SANS_SOLDE: { label: 'Congé sans solde', short: 'S. solde', dot: '#b8741e', chip: 'bg-amber-100 text-amber-800 border-amber-300', cell: 'bg-amber-200 text-amber-800 border-amber-400' },
  SICK: { label: 'Maladie', short: 'Maladie', dot: '#dc2626', chip: 'bg-red-100 text-red-700 border-red-300', cell: 'bg-red-200 text-red-800 border-red-400' },
  ACCIDENT_TRAVAIL: { label: 'Accident du travail', short: 'AT', dot: '#dc2626', chip: 'bg-red-100 text-red-700 border-red-300', cell: 'bg-red-200 text-red-800 border-red-400' },
  MATERNITY: { label: 'Maternité', short: 'Maternité', dot: '#db2777', chip: 'bg-pink-100 text-pink-700 border-pink-300', cell: 'bg-pink-200 text-pink-800 border-pink-400' },
  FAMILIAL: { label: 'Événement familial', short: 'Familial', dot: '#4f46e5', chip: 'bg-indigo-100 text-indigo-700 border-indigo-300', cell: 'bg-indigo-200 text-indigo-800 border-indigo-400' },
  RECUPERATION: { label: 'Récupération', short: 'Récup', dot: '#0d9488', chip: 'bg-teal-100 text-teal-700 border-teal-300', cell: 'bg-teal-200 text-teal-800 border-teal-400' },
  FORMATION: { label: 'Formation', short: 'Formation', dot: '#2563eb', chip: 'bg-blue-100 text-blue-700 border-blue-300', cell: 'bg-blue-200 text-blue-800 border-blue-400' },
  MEDICAL: { label: 'RDV médical', short: 'Médical', dot: '#e11d48', chip: 'bg-rose-100 text-rose-700 border-rose-300', cell: 'bg-rose-200 text-rose-800 border-rose-400' },
  PERSO: { label: 'Indisponibilité perso', short: 'Perso', dot: '#7c3aed', chip: 'bg-violet-100 text-violet-700 border-violet-300', cell: 'bg-violet-200 text-violet-800 border-violet-400' },
  AUTRE: { label: 'Autre', short: 'Autre', dot: '#64748b', chip: 'bg-slate-100 text-slate-600 border-slate-300', cell: 'bg-slate-200 text-slate-700 border-slate-400' },
  // Hérité (non exposé dans l'UI) — repli neutre.
  ABSENCE: { label: 'Absence', short: 'Absence', dot: '#64748b', chip: 'bg-slate-100 text-slate-600 border-slate-300', cell: 'bg-slate-200 text-slate-700 border-slate-400' },
};

// Case « effacée » — utilisée dans la GRILLE DU PLANNING uniquement.
// Là-bas, ce sont les PRÉSENTS qui doivent ressortir : un aplat saturé par
// absence noyait la lecture du volume de personnel. Les absents reculent donc
// tous de la même façon (peu importe le motif) et la couleur du motif ne
// subsiste que dans la pastille + le libellé. Ailleurs (calendrier
// Disponibilités, file Demandes), `cell` reste saturé : c'est l'info utile.
export const ABSENCE_CELL_MUTED = 'bg-slate-50 text-slate-400 border-slate-200';

export const absenceMeta = (t: AbsenceType | null | undefined): AbsenceMeta =>
  (t && ABSENCE_META[t]) || ABSENCE_META.AUTRE;
export const absenceLabel = (t: AbsenceType | null | undefined): string => absenceMeta(t).label;
